import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconClose, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { fmtMoney } from '../../../common/utils/money';
import { formatDateOnly } from '../../../common/utils/date';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { MoneyInput } from './sale-form';
import { useSalePayments, useCreateSalePayment, useDeleteSalePayment } from '../contexts/sales';

import '../styles/sales.css';

const today = () => new Date().toLocaleDateString('en-CA'); // wall-clock YYYY-MM-DD (0004)

// Medios de pago SUGERIDOS (datalist). El medio es un string LIBRE (Q-199), no un enum:
// el vendedor puede escribir cualquier cosa; esto solo autocompleta lo habitual.
const METHOD_SUGGESTIONS = ['Efectivo', 'Transferencia', 'Cheque', 'Consignación'];

const targetName = (s) => (
    s?.calf ? (s.calf.name || s.calf.number || `#${s.calf.id}`)
        : s?.projected_cross ? (s.projected_cross.code || s.projected_cross.name || `#${s.projected_cross.id}`)
            : `Venta #${s?.id}`
);

// Panel de ABONOS de una venta (sub-recurso Q-199): historial + alta con medio de pago.
// Los abonos son descriptivos (no postean al ledger) y encienden needs_recalculation en la
// venta. El backend NO calcula saldo (0001): el "Total abonado" es una Σ pura de vista.
export default function PaymentsPanel({ sale, onClose }) {
    const { data, isLoading, error } = useSalePayments(sale.id);
    const { mutateAsync: create, isPending: creating } = useCreateSalePayment(sale.id);
    const { mutateAsync: remove, isPending: removing } = useDeleteSalePayment(sale.id);
    const busy = creating || removing;
    const cardRef = useModalDialog(onClose);
    const confirm = useConfirm();

    const [amount, setAmount] = useState('');
    const [paidAt, setPaidAt] = useState(today());
    const [method, setMethod] = useState('');

    const items = data?.items ?? [];
    const totalPaid = items.reduce((acc, p) => acc + Number(p.amount || 0), 0); // Σ de vista (0001)

    const submit = async () => {
        if (amount === '' || Number(amount) <= 0) { toast.error('El monto del abono debe ser mayor a 0'); return; }
        if (!paidAt) { toast.error('La fecha del abono es obligatoria'); return; }
        try {
            await create({ amount: Number(amount), paid_at: paidAt, method: method.trim() || null });
            toast.success('Abono registrado');
            setAmount(''); setMethod(''); setPaidAt(today());
        } catch (er) {
            if (er?.status === 400) toast.error(er?.message || 'Revisá el monto y la fecha del abono');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo registrar el abono');
        }
    };

    const anular = async (p) => {
        const ok = await confirm({
            title: 'Anular abono',
            message: `¿Anular el abono de ${fmtMoney(p.amount)} del ${formatDateOnly(p.paid_at)}?`,
            confirmLabel: 'Anular',
            danger: true,
        });
        if (!ok) return;
        try { await remove(p.id); toast.success('Abono anulado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo anular el abono'); }
    };

    return (
        <div className="sale-modal" role="dialog" aria-modal="true" aria-label="Abonos de la venta">
            <div className="sale-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="sale-modal__card" ref={cardRef}>
                <header className="sale-modal__head">
                    <div>
                        <h2 className="sale-modal__title">Abonos</h2>
                        <span className="sale-modal__sub">{targetName(sale)} · {sale.buyer?.name || `Cliente #${sale.buyer?.id}`}</span>
                    </div>
                    <button type="button" className="sale-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="sale-pay">
                    {/* Alta de abono — solo escritura */}
                    <Gate domain="clients" action="write">
                        <div className="sale-form__section">
                            <span className="sale-form__section-title">Registrar abono</span>
                            <div className="sale-form__row">
                                <div className="sale-form__field">
                                    <span className="sale-form__label">Monto</span>
                                    <MoneyInput value={amount} onChange={setAmount} disabled={busy} placeholder="Mayor a 0" />
                                </div>
                                <label className="sale-form__field">
                                    <span className="sale-form__label">Fecha del abono</span>
                                    <input type="date" max={today()} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} disabled={busy} />
                                </label>
                            </div>
                            <label className="sale-form__field">
                                <span className="sale-form__label">Medio de pago</span>
                                <input type="text" list="sale-pay-methods" maxLength={40} value={method} onChange={(e) => setMethod(e.target.value)} disabled={busy} placeholder="Efectivo, transferencia, cheque…" />
                                <datalist id="sale-pay-methods">
                                    {METHOD_SUGGESTIONS.map((m) => <option key={m} value={m} />)}
                                </datalist>
                            </label>
                            <button type="button" className="sale-btn sale-btn--primary" onClick={submit} disabled={busy}>
                                {creating ? 'Registrando…' : 'Registrar abono'}
                            </button>
                        </div>
                    </Gate>

                    {/* Historial */}
                    <div className="sale-form__section">
                        <span className="sale-form__section-title">Historial</span>
                        {isLoading ? (
                            <p className="sale-pay__empty">Cargando abonos…</p>
                        ) : error ? (
                            <p className="sale-pay__empty">{error.status === 403 ? 'No tenés permiso para ver los abonos.' : (error.message || 'No se pudieron cargar los abonos')}</p>
                        ) : !items.length ? (
                            <p className="sale-pay__empty">Aún no hay abonos registrados.</p>
                        ) : (
                            <>
                                <div className="sale-pay__list">
                                    {items.map((p) => (
                                        <div key={p.id} className="sale-pay__row">
                                            <div className="sale-pay__meta">
                                                <span className="sale-pay__date">{formatDateOnly(p.paid_at)}</span>
                                                {p.method && <span className="sale-pay__method">{p.method}</span>}
                                            </div>
                                            <div className="sale-pay__right">
                                                <span className="sale-pay__amount">{fmtMoney(p.amount)}</span>
                                                <Gate domain="clients" action="write">
                                                    <button type="button" className="sale-icon sale-icon--danger" onClick={() => anular(p)} disabled={busy} aria-label="Anular abono" title="Anular"><IconTrash /></button>
                                                </Gate>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="sale-pay__total">
                                    <span>Total abonado</span>
                                    <span>{fmtMoney(totalPaid)}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <footer className="sale-modal__foot">
                    <button type="button" className="sale-btn sale-btn--ghost" onClick={onClose} disabled={busy}>Cerrar</button>
                </footer>
            </div>
        </div>
    );
}
